import { useEffect, useMemo, useRef, useState } from 'react';
import { proyector, renderiza, type Vista } from './sistema3d/motor';
import { COMPONENTES, TECHO_PANELES, escena, explicacion, flujos, type IdComponente } from './sistema3d/escena';
import { usePedidoWhatsApp } from '@/hooks/usePedidoWhatsApp';
import SelectorVendedor from '@/components/cart/SelectorVendedor';

const GRADOS = Math.PI / 180;
const THETA_INICIAL = 22 * GRADOS;
// Desde 3°: nunca se ve la casa por el lado donde no hay pared.
const THETA_MIN = 3 * GRADOS;
const THETA_MAX = 55 * GRADOS;
const PHI = 32 * GRADOS;
const ESCALA = 10;

const limita = (t: number) => Math.min(THETA_MAX, Math.max(THETA_MIN, t));

// Colores de ilustración: contorno, corriente continua y corriente alterna
const CONTORNO = '#1f2a44';
const NARANJA = '#f97316';
const TURQUESA = '#14b8a6';
/** Cables de corriente continua (paneles y batería); el resto es alterna. */
const DC = new Set(['pv', 'dc', 'bat']);

/** Sol con rayos que apuntan a los paneles; de noche, luna y estrellas. */
function Cielo({ noche, x, y, hacia }: { noche: boolean; x: number; y: number; hacia: [number, number] }) {
  if (noche) {
    return (
      <g aria-hidden="true">
        {[[60, -30], [150, 10], [230, -40], [320, 5], [420, -25], [520, 20], [640, -35]].map(([dx, dy], i) => (
          <circle key={i} cx={x + dx} cy={y + dy} r={i % 2 ? 1.6 : 2.2} fill="#fef9c3" opacity={0.85} />
        ))}
        <circle cx={x} cy={y} r={24} fill="#f5e7c6" stroke={CONTORNO} strokeWidth={1.2} />
        <circle cx={x + 11} cy={y - 7} r={21} fill="#15223d" />
      </g>
    );
  }
  const ang = Math.atan2(hacia[1] - y, hacia[0] - x);
  const d = Math.hypot(hacia[0] - x, hacia[1] - y);
  return (
    <g aria-hidden="true">
      <circle cx={x} cy={y} r={40} fill="#fde047" opacity={0.25} />
      <circle cx={x} cy={y} r={28} fill="#fbbf24" stroke={CONTORNO} strokeWidth={1.2} />
      {[-0.22, 0, 0.22].map((o, i) => {
        const a = ang + o;
        const x0 = x + Math.cos(a) * 40, y0 = y + Math.sin(a) * 40;
        const x1 = x + Math.cos(a) * d * 0.55, y1 = y + Math.sin(a) * d * 0.55;
        const g = (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI;
        return (
          <g key={i}>
            <path d={`M${x0} ${y0} L${x1} ${y1}`} stroke="#f59e0b" strokeWidth={3} strokeLinecap="round" strokeDasharray="10 8" className="flujo" />
            <path d="M-10 -6 L2 0 L-10 6 Z" fill="#f59e0b" transform={`translate(${x1} ${y1}) rotate(${g})`} />
          </g>
        );
      })}
    </g>
  );
}

/** Plantas en maceta y arbustos, dibujados planos como en una ilustración. */
function FiguraPlana({ x, y, tipo, escala, noche }: { x: number; y: number; tipo: 'maceta' | 'arbusto'; escala: number; noche: boolean }) {
  const hoja = noche ? '#2f6b3a' : '#4d9b4f';
  const hoja2 = noche ? '#3f8048' : '#66b566';
  return (
    <g transform={`translate(${x} ${y}) scale(${escala})`} aria-hidden="true">
      {tipo === 'maceta' ? (
        <>
          <path d="M-9 -26 C-22 -40 -12 -58 -2 -44 C0 -62 16 -60 10 -40 C22 -48 24 -30 10 -24 Z" fill={hoja} stroke={CONTORNO} strokeWidth={1.1} />
          <path d="M-4 -28 C-8 -44 4 -50 4 -34" fill="none" stroke={hoja2} strokeWidth={2} />
          <path d="M-9 -14 L9 -14 L7 0 L-7 0 Z" fill="#f8fafc" stroke={CONTORNO} strokeWidth={1.1} />
          <rect x={-10} y={-18} width={20} height={5} rx={1.5} fill="#e5e7eb" stroke={CONTORNO} strokeWidth={1.1} />
        </>
      ) : (
        <>
          <circle cx={-12} cy={-12} r={13} fill={hoja} stroke={CONTORNO} strokeWidth={1.1} />
          <circle cx={12} cy={-13} r={14} fill={hoja} stroke={CONTORNO} strokeWidth={1.1} />
          <circle cx={0} cy={-24} r={15} fill={hoja2} stroke={CONTORNO} strokeWidth={1.1} />
        </>
      )}
    </g>
  );
}

/** Recuadro que contiene la escena en cualquier giro permitido (así no "salta" al girar). */
function marco(): string {
  const { mallas } = escena(false);
  const pts = mallas.flatMap((m) => m.caras.flatMap((c) => c.pts));
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (let t = THETA_MIN; t <= THETA_MAX + 1e-6; t += 5 * GRADOS) {
    const P = proyector({ theta: t, phi: PHI, escala: ESCALA });
    for (const p of pts) {
      const [x, y] = P.pantalla(p);
      x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y);
    }
  }
  const m = 14;
  return `${(x0 - m).toFixed(0)} ${(y0 - m - 16).toFixed(0)} ${(x1 - x0 + 2 * m).toFixed(0)} ${(y1 - y0 + 2 * m + 16).toFixed(0)}`;
}

export default function SistemaSolar3D() {
  const [noche, setNoche] = useState(false);
  const [apagon, setApagon] = useState(false);
  const [theta, setTheta] = useState(THETA_INICIAL);
  const [sel, setSel] = useState<IdComponente | null>('paneles');
  const tocado = useRef(false);
  const arrastre = useRef<{ x: number; t: number } | null>(null);
  const contenedor = useRef<HTMLDivElement>(null);
  const pedido = usePedidoWhatsApp();

  const viewBox = useMemo(marco, []);
  const svgRef = useRef<SVGSVGElement>(null);
  // Cuántas unidades del dibujo mide 1 px en pantalla: los números y marcas
  // se ven del mismo tamaño en el celular y en la computadora.
  const [k, setK] = useState(1);
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const ancho = Number(viewBox.split(' ')[2]);
    const ro = new ResizeObserver(() => setK(ancho / Math.max(1, el.clientWidth)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [viewBox]);
  const { mallas, lineas, figuras } = useMemo(() => escena(noche), [noche]);
  const flujo = useMemo(() => flujos({ noche, apagon }), [noche, apagon]);
  const estado = explicacion({ noche, apagon });
  const vista: Vista = { theta, phi: PHI, escala: ESCALA };
  const dibujo = useMemo(() => renderiza(mallas, lineas, vista, figuras),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mallas, lineas, figuras, theta]);
  const vb = viewBox.split(' ').map(Number);
  const P = proyector(vista);

  // Balanceo suave mientras nadie lo toca, solo si la sección está en pantalla.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let visible = false;
    let id = 0;
    const obs = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    if (contenedor.current) obs.observe(contenedor.current);
    const inicio = performance.now();
    const paso = (ahora: number) => {
      if (tocado.current) return;
      if (visible) setTheta(THETA_INICIAL + Math.sin((ahora - inicio) / 2600) * 14 * GRADOS);
      id = requestAnimationFrame(paso);
    };
    id = requestAnimationFrame(paso);
    return () => { cancelAnimationFrame(id); obs.disconnect(); };
  }, []);

  const detener = () => { tocado.current = true; };
  const comp = COMPONENTES.find((c) => c.id === sel);

  const quiero = () => {
    pedido.enviar(encodeURIComponent(
      '¡Hola! Vi en la web cómo funciona el sistema solar con 4 paneles de 630 W, inversor híbrido y batería MUST. ¿Me pueden dar un presupuesto?',
    ));
  };

  return (
    <section className="py-16 md:py-20 bg-white" aria-labelledby="sistema3d-titulo">
      <style>{`
        @keyframes flujoEnergia { to { stroke-dashoffset: -24; } }
        .flujo { animation: flujoEnergia 1.1s linear infinite; }
        .flujo.reves { animation-direction: reverse; }
        @keyframes pulsoPin { 0%,100% { r: 11; opacity: .35; } 50% { r: 17; opacity: 0; } }
        .pulso { animation: pulsoPin 2s ease-out infinite; }
        @media (prefers-reduced-motion: reduce) { .flujo, .pulso { animation: none; } }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-8">
          <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#4bbfdf' }}>
            Así funciona
          </span>
          <h2 id="sistema3d-titulo" className="font-extrabold text-stone-900 mt-2" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', letterSpacing: '-0.025em', lineHeight: 1.15 }}>
            ¿Cómo funciona un sistema solar con baterías?
          </h2>
          <p className="text-stone-500 mt-3 text-[15px] leading-relaxed">
            Este es el sistema que más instalamos en La Habana. Gíralo con el dedo, cambia entre día, noche y apagón,
            y toca cada número para ver qué hace cada equipo.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
          {/* Modelo 3D */}
          <div
            ref={contenedor}
            className="relative rounded-3xl overflow-hidden border border-[#e2ddd5]"
            style={{
              background: noche
                ? 'linear-gradient(180deg, #0b1426 0%, #1b2a4a 60%, #22345a 100%)'
                : 'linear-gradient(180deg, #dff3fb 0%, #f3f9fc 60%, #ffffff 100%)',
              transition: 'background 0.6s ease',
            }}
          >
            {/* Momento del día y corriente de la calle: se combinan */}
            <div className="relative z-10 m-3 md:m-4 flex flex-wrap items-center gap-2">
              <div className="flex gap-1 p-1 rounded-full bg-white/90 border border-[#e2ddd5] shadow-sm" role="radiogroup" aria-label="Momento del día">
                {[false, true].map((n) => (
                  <button
                    key={String(n)}
                    role="radio"
                    aria-checked={noche === n}
                    onClick={() => setNoche(n)}
                    className="px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-colors cursor-pointer"
                    style={noche === n ? { background: '#1e2d4a', color: '#fff' } : { color: '#57534e' }}
                  >
                    {n ? '🌙 Noche' : '☀️ Día'}
                  </button>
                ))}
              </div>
              <button
                role="switch"
                aria-checked={apagon}
                onClick={() => setApagon((v) => !v)}
                className="flex items-center gap-2 pl-1.5 pr-3.5 py-1 rounded-full text-[12px] font-bold border shadow-sm transition-colors cursor-pointer"
                style={apagon
                  ? { background: '#ef4444', color: '#fff', borderColor: '#ef4444' }
                  : { background: 'rgba(255,255,255,0.9)', color: '#57534e', borderColor: '#e2ddd5' }}
              >
                <span
                  className="relative inline-block w-8 h-[18px] rounded-full transition-colors"
                  style={{ background: apagon ? 'rgba(255,255,255,0.35)' : '#d6d3d1' }}
                  aria-hidden="true"
                >
                  <span
                    className="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-all"
                    style={{ left: apagon ? 16 : 2 }}
                  />
                </span>
                ⚡ Apagón
              </button>
            </div>

            <svg
              ref={svgRef}
              viewBox={viewBox}
              className="w-full h-auto block select-none outline-none focus-visible:ring-2 focus-visible:ring-[#4bbfdf] focus-visible:ring-inset"
              style={{ touchAction: 'pan-y', cursor: arrastre.current ? 'grabbing' : 'grab' }}
              role="img"
              aria-label="Modelo 3D de una vivienda con sistema solar: paneles sobre la losa, caja de protecciones, inversor, batería, puesta a tierra, metro contador y cargador de coche eléctrico"
              tabIndex={0}
              onPointerDown={(e) => {
                detener();
                arrastre.current = { x: e.clientX, t: theta };
                (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId);
              }}
              onPointerMove={(e) => {
                if (!arrastre.current) return;
                setTheta(limita(arrastre.current.t + (e.clientX - arrastre.current.x) * 0.006));
              }}
              onPointerUp={() => { arrastre.current = null; }}
              onPointerCancel={() => { arrastre.current = null; }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                  detener();
                  setTheta((t) => limita(t + (e.key === 'ArrowLeft' ? -8 : 8) * GRADOS));
                }
              }}
            >
              {/* Sol con sus rayos hacia los paneles, o luna y estrellas */}
              <Cielo noche={noche} x={vb[0] + 46} y={vb[1] + 70} hacia={P.pantalla([-1, 9, TECHO_PANELES])} />

              {dibujo.map((d, i) => {
                if (d.tipo === 'cara') {
                  return (
                    <path
                      key={i} d={d.d} fill={d.fill} strokeLinejoin="round"
                      stroke={d.borde ? CONTORNO : d.fill} strokeWidth={d.borde ? 1.1 : 1}
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                }
                if (d.tipo === 'figura') {
                  return <FiguraPlana key={i} x={d.x} y={d.y} tipo={d.figura} escala={d.escala} noche={noche} />;
                }
                const f = d.id ? flujo[d.id] : 0;
                const activo = f === 1 || f === -1;
                const color = activo ? (DC.has(d.id!) ? NARANJA : TURQUESA) : f === 'corte' ? '#ef4444' : d.color;
                return (
                  <g key={i}>
                    <path
                      d={d.d} fill="none" strokeLinecap="round" strokeLinejoin="round"
                      stroke={color}
                      strokeWidth={activo ? d.ancho + 1.4 : d.ancho}
                      strokeDasharray={f === 'corte' ? '3 4' : undefined}
                      opacity={f === 'corte' ? 0.85 : 1}
                    />
                    {activo && (
                      <path
                        d={d.d} fill="none" strokeLinecap="round"
                        stroke="#ffffff" strokeWidth={Math.max(1.3, d.ancho * 0.75)}
                        strokeDasharray="0.1 5.9"
                        className={`flujo${f === -1 ? ' reves' : ''}`}
                      />
                    )}
                  </g>
                );
              })}

              {/* Flechas en la punta de cada cable con energía */}
              {lineas.map((l, i) => {
                const f = l.id ? flujo[l.id] : 0;
                if (!l.flecha || (f !== 1 && f !== -1)) return null;
                const pts = f === 1 ? l.pts : [...l.pts].reverse();
                const [x0, y0] = P.pantalla(pts[pts.length - 2]);
                const [x1, y1] = P.pantalla(pts[pts.length - 1]);
                const ang = (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI;
                return (
                  <g key={`fl${i}`} transform={`translate(${x1} ${y1}) rotate(${ang}) scale(${k})`} aria-hidden="true">
                    <path d="M-9 -5.5 L1 0 L-9 5.5 Z" fill={DC.has(l.id!) ? NARANJA : TURQUESA} stroke={CONTORNO} strokeWidth={1} strokeLinejoin="round" />
                  </g>
                );
              })}

              {/* Sin corriente en la calle */}
              {apagon && (() => {
                const [x, y] = P.pantalla([30, 1, 22]);
                return (
                  <g transform={`translate(${x} ${y}) scale(${k})`} aria-hidden="true">
                    <circle r={9} fill="#ef4444" />
                    <path d="M-4 -4 L4 4 M4 -4 L-4 4" stroke="#fff" strokeWidth={2.4} strokeLinecap="round" />
                  </g>
                );
              })()}

              {/* Puntos para tocar */}
              {COMPONENTES.map((c, i) => {
                const [x, y] = P.pantalla(c.ancla);
                const activo = sel === c.id;
                return (
                  <g
                    key={c.id}
                    transform={`translate(${x} ${y}) scale(${k})`}
                    onClick={() => { detener(); setSel(c.id); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    style={{ cursor: 'pointer' }}
                    role="button"
                    aria-label={c.titulo}
                  >
                    {!activo && <circle className="pulso" r={11} fill="#4bbfdf" />}
                    <circle r={10} fill={activo ? '#1e2d4a' : '#4bbfdf'} stroke="#fff" strokeWidth={2.2} />
                    <text textAnchor="middle" dy="4" fontSize="11" fontWeight="800" fill="#fff" style={{ pointerEvents: 'none' }}>
                      {i + 1}
                    </text>
                  </g>
                );
              })}
            </svg>

            <p className="px-4 pb-3 -mt-2 text-[11px] font-medium flex items-center gap-1.5 pointer-events-none"
              style={{ color: noche ? 'rgba(255,255,255,0.6)' : '#78716c' }}>
              <i className="ri-drag-move-2-line" /> Arrastra para girar
            </p>
          </div>

          {/* Explicación */}
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl p-4 border" style={{ borderColor: apagon ? '#fecaca' : '#e2ddd5', background: apagon ? '#fef2f2' : '#fff' }} aria-live="polite">
              <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: apagon ? '#dc2626' : '#4bbfdf' }}>
                {estado.titulo}
              </p>
              <p className="text-sm text-stone-700 mt-1 leading-relaxed">{estado.texto}</p>
              <div className="text-[11px] text-stone-500 mt-2.5 flex flex-col gap-1">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-5 h-1.5 rounded-full" style={{ background: NARANJA }} />
                  Corriente continua: paneles y batería
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-5 h-1.5 rounded-full" style={{ background: TURQUESA }} />
                  Corriente alterna: casa, coche y red
                </span>
              </div>
            </div>

            {comp && (
              <div className="rounded-2xl p-4 border border-[#e2ddd5] bg-white" aria-live="polite">
                <p className="font-bold text-stone-900">{comp.titulo}</p>
                <p className="text-sm text-stone-600 mt-1 leading-relaxed">{comp.texto}</p>
              </div>
            )}

            <ol className="grid grid-cols-2 gap-1.5">
              {COMPONENTES.map((c, i) => (
                <li key={c.id}>
                  <button
                    onClick={() => { detener(); setSel(c.id); }}
                    className="w-full flex items-center gap-2 text-left text-[12px] font-semibold px-2.5 py-2 rounded-xl border transition-colors cursor-pointer"
                    style={sel === c.id
                      ? { background: '#1e2d4a', color: '#fff', borderColor: '#1e2d4a' }
                      : { background: '#fff', color: '#44403c', borderColor: '#e2ddd5' }}
                  >
                    <span className="w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-black"
                      style={{ background: sel === c.id ? '#4bbfdf' : '#e8f8fd', color: sel === c.id ? '#fff' : '#1e8aab' }}>
                      {i + 1}
                    </span>
                    <span className="leading-tight">{c.titulo.split(' · ')[0]}</span>
                  </button>
                </li>
              ))}
            </ol>

            <button
              onClick={quiero}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-[14px] text-white cursor-pointer active:scale-[0.98] transition-transform"
              style={{ background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)' }}
            >
              <i className="ri-whatsapp-line text-lg" />
              Quiero este sistema
            </button>
          </div>
        </div>
      </div>
      <SelectorVendedor {...pedido.selector} />
    </section>
  );
}
